<div class="grades-page">
  <h1 class="page-title">Marks & grades</h1>
  <p class="page-sub">Define subject columns, enter scores per student, then review class rankings (ties share rank).</p>

  <mat-card class="filters-card">
    <mat-card-content>
      <form [formGroup]="filterForm" class="filters-row" (ngSubmit)="$event.preventDefault()">
        <mat-form-field appearance="outline" class="field-year">
          <mat-label>Academic year</mat-label>
          <input matInput formControlName="academicYear" placeholder="2025-2026" (change)="onFilterChange()" />
        </mat-form-field>
        <mat-form-field appearance="outline" class="field-class">
          <mat-label>Class</mat-label>
          <mat-select formControlName="className" (selectionChange)="onFilterChange()">
            <mat-option value="">— Select —</mat-option>
            <mat-option *ngFor="let c of classOptions" [value]="c">{{ c }}</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline" class="field-sheet" *ngIf="filterForm.valid">
          <mat-label>Mark sheet</mat-label>
          <mat-select
            [value]="selectedSheetKey || ''"
            (selectionChange)="onSelectSheet($event.value === '' ? null : $event.value)"
          >
            <mat-option value="">— Select —</mat-option>
            <mat-option *ngFor="let s of filteredSheets" [value]="s.id">{{ s.examTitle }}</mat-option>
            <mat-option value="NEW">+ New examination</mat-option>
          </mat-select>
        </mat-form-field>
      </form>
    </mat-card-content>
  </mat-card>

  <div *ngIf="loading && !activeSheet" class="state-block">
    <mat-spinner diameter="40"></mat-spinner>
    <span>Loading mark sheets…</span>
  </div>

  <ng-container *ngIf="activeSheet">
    <mat-card class="meta-card">
      <mat-card-content class="meta-row">
        <mat-form-field appearance="outline" class="field-title">
          <mat-label>Examination title</mat-label>
          <input matInput [(ngModel)]="examTitleInput" name="examTitle" placeholder="e.g. Term 1 — Unit test" />
        </mat-form-field>
        <span class="meta-hint">Year: {{ activeSheet.academicYear }} · Class: {{ activeSheet.className }}</span>
      </mat-card-content>
    </mat-card>

    <mat-tab-group [(selectedIndex)]="tabIndex" animationDuration="200ms">
      <mat-tab label="Marks entry">
        <div class="tab-body">
          <mat-card class="section-card">
            <mat-card-title>Subject columns</mat-card-title>
            <mat-card-content>
              <p class="hint">Adjust labels and maximum marks per paper. Scores above max are clamped on save.</p>
              <div class="columns-toolbar">
                <button mat-stroked-button type="button" color="primary" (click)="addColumn()">
                  <mat-icon>add</mat-icon>
                  Add subject
                </button>
              </div>
              <div class="column-rows">
                <div *ngFor="let col of activeSheet.columns; trackBy: trackByColId" class="column-row">
                  <mat-form-field appearance="outline" class="col-subject">
                    <mat-label>Subject</mat-label>
                    <input matInput [(ngModel)]="col.subject" [name]="'subj' + col.id" />
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="col-max">
                    <mat-label>Max marks</mat-label>
                    <input matInput type="number" min="1" [(ngModel)]="col.maxMarks" [name]="'max' + col.id" />
                  </mat-form-field>
                  <button
                    mat-icon-button
                    type="button"
                    color="warn"
                    (click)="removeColumn(col)"
                    [disabled]="activeSheet.columns.length <= 1"
                    matTooltip="Remove column"
                  >
                    <mat-icon>delete_outline</mat-icon>
                  </button>
                </div>
              </div>
            </mat-card-content>
          </mat-card>

          <mat-card class="section-card">
            <mat-card-title>Student scores</mat-card-title>
            <mat-card-content>
              <div class="scores-toolbar">
                <button mat-raised-button type="button" color="primary" (click)="loadStudents()" [disabled]="loading">
                  <mat-icon>groups</mat-icon>
                  Load class roster
                </button>
                <button mat-raised-button type="button" color="accent" (click)="save()" [disabled]="saving">
                  <mat-icon>save</mat-icon>
                  Save mark sheet
                </button>
                <button
                  *ngIf="isAdmin && !isNewSheet"
                  mat-button
                  type="button"
                  color="warn"
                  (click)="deleteActiveSheet()"
                  [disabled]="saving"
                >
                  Delete sheet
                </button>
              </div>

              <div class="table-scroll" *ngIf="activeSheet.rows.length">
                <table class="marks-table">
                  <thead>
                    <tr>
                      <th class="th-roll">Roll</th>
                      <th class="th-name">Student</th>
                      <th *ngFor="let col of activeSheet.columns; trackBy: trackByColId" class="th-score">
                        {{ col.subject }}
                        <span class="th-max">(max {{ col.maxMarks }})</span>
                      </th>
                      <th class="th-pct">%</th>
                      <th class="th-grade">Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let row of activeSheet.rows; trackBy: trackByStudentId">
                      <td>{{ row.rollNumber || '—' }}</td>
                      <td class="td-name">{{ row.firstName }} {{ row.lastName }}</td>
                      <td *ngFor="let col of activeSheet.columns; trackBy: trackByColId">
                        <input
                          class="score-input"
                          type="number"
                          [ngModel]="row.scores[col.id]"
                          (ngModelChange)="patchScore(row, col.id, col.maxMarks, $event)"
                          [name]="'sc' + row.studentId + col.id"
                          min="0"
                          [attr.max]="col.maxMarks"
                          step="0.25"
                        />
                      </td>
                      <td class="td-num">{{ rowPercent(row) }}%</td>
                      <td class="td-grade">{{ rowLetter(row) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p *ngIf="!activeSheet.rows.length" class="empty-roster">Load the class roster to enter marks.</p>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>

      <mat-tab label="Rankings">
        <div class="tab-body">
          <mat-card class="section-card">
            <mat-card-title>Class ranking</mat-card-title>
            <mat-card-content>
              <p class="hint">Sorted by total percentage. Letter grades: A+ ≥90, A ≥80, B ≥70, C ≥60, D ≥50, F &lt;50.</p>
              <div class="table-scroll" *ngIf="rankedRows.length">
                <table class="rank-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Roll</th>
                      <th>Student</th>
                      <th>Total</th>
                      <th>%</th>
                      <th>Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let r of rankedRows">
                      <td class="td-rank">{{ r.rank }}</td>
                      <td>{{ r.rollNumber || '—' }}</td>
                      <td>{{ r.firstName }} {{ r.lastName }}</td>
                      <td class="td-num">{{ r.totalObtained }} / {{ r.totalMax }}</td>
                      <td class="td-num">{{ r.percent | number : '1.0-2' }}</td>
                      <td class="td-grade">{{ r.letterGrade }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p *ngIf="!rankedRows.length" class="empty-roster">No students or marks yet. Use the Marks entry tab.</p>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>
    </mat-tab-group>
  </ng-container>

  <mat-card *ngIf="!loading && !activeSheet && filterForm.valid" class="hint-card">
    <mat-card-content> Choose an existing mark sheet or create a new examination. </mat-card-content>
  </mat-card>
</div>
